import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Icon, palette } from '@m12s/component-library';
import { times, fileAltRegular } from 'lib/icons';
import { getLatestActivityMode } from 'lib/selectors/getLatestActivityMode';
import { getLatestActivityModeStartAt } from 'lib/selectors/getLatestActivityModeStartAt';
import { getProductionPartsDisplay } from 'lib/selectors/getProductionPartsDisplay';
import {
  getCurrentWorkOrderOp,
  getCurrentWorkOrderOpStartAt,
  getCurrentWorkOrderOpTimeToComplete,
} from 'lib/selectors/getCurrentWorkOrderOp';
import { useJobDisplayName } from 'lib/hooks/useJobDisplayName';
import { hasValue } from 'lib/jobDocs/hasValue';

// Header color signals whether the machine is actively producing: setup (being
// set up, not producing) → yellow, production (actively producing) → green,
// matching the Figma tokens (Yellow/400 #F6E05E, Green/600 #38A169 in the
// @m12s scale). Any other or unknown mode has no specific activity →
// neutral grey. Keys are LOWERCASE activity modes.
const HEADER_BACKGROUND = {
  setup: palette.Yellow400,
  production: palette.Green600,
};
const HEADER_BACKGROUND_DEFAULT = palette.Grey200;

// Text is dark on the light yellow/grey bars but WHITE on the green
// production bar (Figma).
const HEADER_TEXT_COLOR = {
  production: palette.White,
};
const HEADER_TEXT_COLOR_DEFAULT = palette.Grey900;

const HeaderBar = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  column-gap: 1.5rem;
  min-height: 3.5rem;
  padding: 0.5rem 0.5rem 0.5rem 1rem;
`;

// flex:1 + min-width:0 lets the cluster shrink so a long TITLE truncates with
// an ellipsis instead of wrapping the single-line header (Figma).
const TitleCluster = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  gap: 0.625rem;
  font-size: 0.875rem;
  font-weight: 600;
`;

const FileIconBadge = styled.span`
  display: flex;
  flex: none;
  align-items: center;
`;

// The title yields first: it shrinks (ellipsis) while the operation label
// keeps its natural width until the title is fully collapsed.
const Title = styled.span`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Separator = styled.span`
  flex: none;
  opacity: 0.4;
`;

const OperationLabel = styled.span`
  flex: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MetaCluster = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  gap: 1rem;
  font-size: 0.875rem;
  white-space: nowrap;
`;

// Figma weight rule: LABELS (mode word, "Active:", "Made:", …) are semibold
// (600) and the VALUES are regular weight.
const ModeLabel = styled.span`
  font-weight: 600;
`;

const StatLabel = styled.span`
  font-weight: 600;
`;

// Ghost icon button — transparent over the mode-colored bar. Sized to a
// 40px (2.5rem) minimum hit target for touch (WCAG 2.5.5 / operator gloves).
const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2.5rem;
  min-height: 2.5rem;
  padding: 0.25rem;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: inherit;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.08);
  }

  &:focus-visible {
    outline: 2px solid ${palette.Blue500};
    outline-offset: 2px;
  }
`;

/** Format a millisecond duration Figma-style: `1h 5m` or `1m 13s`. */
export const formatHeaderDuration = (ms) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m ${seconds}s`;
};

const parseMs = (value) => {
  if (value == null || value === '') return null;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

/** Once-per-second tick, active only while `enabled`. */
const useNowMs = (enabled) => {
  const [nowMs, setNowMs] = useState(() => {
    return Date.now();
  });
  useEffect(() => {
    if (!enabled) return undefined;
    setNowMs(Date.now());
    const intervalId = setInterval(() => {
      return setNowMs(Date.now());
    }, 1000);
    return () => {
      return clearInterval(intervalId);
    };
  }, [enabled]);
  return nowMs;
};

const Stat = ({ label, value, testId }) => {
  return (
    <span data-testid={testId}>
      {label ? <StatLabel>{`${label}: `}</StatLabel> : null}
      {value}
    </span>
  );
};

Stat.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node.isRequired,
  testId: PropTypes.string,
};

Stat.defaultProps = { label: null, testId: undefined };

/**
 * Leaf ticker: time in the current activity mode. Owning the per-second tick
 * here means updates re-render only this span — never the drawer or the
 * embedded zone. Renders nothing when the mode start is absent/unparseable.
 */
const ActiveStat = () => {
  const { t } = useTranslation();
  const modeStartMs = parseMs(useSelector(getLatestActivityModeStartAt));
  const nowMs = useNowMs(modeStartMs != null);
  if (modeStartMs == null) return null;
  return (
    <Stat
      label={t('Active')}
      value={formatHeaderDuration(nowMs - modeStartMs)}
      testId="job-docs-header-active"
    />
  );
};

/**
 * Leaf ticker for the mode-specific Figma metric:
 *   Elapsed  — (setup) time since the operation started
 *   Expected — (production) parts expected in the active production window
 *              (active time ÷ expected cycle time)
 */
const ModeExtraStat = ({ mode }) => {
  const { t } = useTranslation();
  const modeStartMs = parseMs(useSelector(getLatestActivityModeStartAt));
  const opStartMs = parseMs(useSelector(getCurrentWorkOrderOpStartAt));
  const cycleTimeMs = useSelector(getCurrentWorkOrderOp)?.cycleTimeMs;
  const showElapsed = mode === 'setup' && opStartMs != null;
  const showExpected =
    mode === 'production' &&
    modeStartMs != null &&
    Number.isFinite(cycleTimeMs) &&
    cycleTimeMs > 0;
  const nowMs = useNowMs(showElapsed || showExpected);

  if (showElapsed) {
    return (
      <Stat
        label={t('Elapsed')}
        value={formatHeaderDuration(nowMs - opStartMs)}
        testId="job-docs-header-elapsed"
      />
    );
  }
  if (showExpected) {
    return (
      <Stat
        label={t('Expected')}
        value={Math.max(0, Math.floor((nowMs - modeStartMs) / cycleTimeMs))}
        testId="job-docs-header-expected"
      />
    );
  }
  return null;
};

ModeExtraStat.propTypes = { mode: PropTypes.string.isRequired };

/**
 * Host-side contextual header for a document opened inside the Job Docs
 * drawer. Rendered by JobDocsDrawer (via EmbeddedMMDrawer's `banner` slot)
 * only while the embed reports an open document through `set-summary`.
 *
 * Figma (ShopPulse Document Viewer): file icon + "title | order op" on the
 * left; on the right the mode word and the live metrics — Active, Made,
 * Elapsed (setup) / Expected (production), Remaining — all from the host
 * store; the embed never receives live data.
 */
const JobDocsDocumentHeader = ({
  title,
  fileLabel,
  partNumber,
  operationId,
  onClose,
}) => {
  const { t } = useTranslation();
  const rawMode = useSelector(getLatestActivityMode);
  // Raw enum is capitalized ('Setup' | 'Production') — normalize for the
  // background/label maps, same as useJobDocsIframe does for the embed URL.
  const mode = typeof rawMode === 'string' ? rawMode.toLowerCase() : '';
  const { displayParts } = useSelector(getProductionPartsDisplay) || {};
  // displayParts can be NaN mid-computation — Number.isFinite, not != null.
  const showParts = Number.isFinite(displayParts);
  const timeToCompleteMs = useSelector(getCurrentWorkOrderOpTimeToComplete);
  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const { getOrderOpDisplayName } = useJobDisplayName();

  // Plain-text mode word — the background already encodes the mode, so per
  // the Figma it is text, not a same-colored badge. Unknown modes show none.
  const modeLabel =
    { setup: t('Setup'), production: t('Production') }[mode] || null;

  // Figma shows the order-op display name ("987649 Op 20"); fall back to the
  // drawer's part/op route params when no current work-order op is loaded.
  const orderOpName = currentWorkOrderOp
    ? getOrderOpDisplayName(currentWorkOrderOp)
    : null;
  const operationLabel =
    orderOpName ||
    [
      hasValue(partNumber) ? String(partNumber) : null,
      hasValue(operationId) ? t('Op {{operationId}}', { operationId }) : null,
    ]
      .filter(Boolean)
      .join(' · ');

  return (
    <HeaderBar
      data-testid="job-docs-contextual-header"
      style={{
        backgroundColor: HEADER_BACKGROUND[mode] || HEADER_BACKGROUND_DEFAULT,
        color: HEADER_TEXT_COLOR[mode] || HEADER_TEXT_COLOR_DEFAULT,
      }}
    >
      <TitleCluster>
        {hasValue(fileLabel) && (
          <FileIconBadge
            data-testid="job-docs-header-file-label"
            role="img"
            aria-label={fileLabel}
          >
            <Icon icon={fileAltRegular} size="1rem" />
          </FileIconBadge>
        )}
        <Title>{title}</Title>
        {operationLabel && (
          <>
            <Separator aria-hidden>|</Separator>
            <OperationLabel data-testid="job-docs-header-operation">
              {operationLabel}
            </OperationLabel>
          </>
        )}
      </TitleCluster>
      <MetaCluster>
        {modeLabel && (
          <ModeLabel data-testid="job-docs-header-mode">{modeLabel}</ModeLabel>
        )}
        <ActiveStat />
        {showParts && (
          <Stat
            label={t('Made')}
            value={displayParts}
            testId="job-docs-header-parts"
          />
        )}
        <ModeExtraStat mode={mode} />
        {timeToCompleteMs != null && Number.isFinite(timeToCompleteMs) && (
          <Stat
            label={t('Remaining')}
            value={formatHeaderDuration(timeToCompleteMs)}
            testId="job-docs-header-remaining"
          />
        )}
        <CloseButton
          type="button"
          onClick={onClose}
          aria-label={t('Close document')}
          data-testid="job-docs-header-close"
        >
          <Icon icon={times} size="1rem" />
        </CloseButton>
      </MetaCluster>
    </HeaderBar>
  );
};

JobDocsDocumentHeader.propTypes = {
  /** Open document's filename, from the embed's `set-summary`. */
  title: PropTypes.string.isRequired,
  /** Short file-type label (e.g. "PDF"), from the embed's `set-summary`. */
  fileLabel: PropTypes.string,
  /** Part number of the operation the drawer was opened for. */
  partNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Operation id / sequence number of that operation. */
  operationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Asks the EMBED to close the document (never dismisses the drawer). */
  onClose: PropTypes.func.isRequired,
};

JobDocsDocumentHeader.defaultProps = {
  fileLabel: null,
  partNumber: null,
  operationId: null,
};

export { JobDocsDocumentHeader };
